import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, CheckCircle, XCircle, Loader, RotateCcw, ScanLine } from 'lucide-react';
import { scanService } from '../lib/supabaseClient';

// ============================================================
// Nederlandse kenteken patronen
// ============================================================
const DUTCH_PLATE_PATTERNS = [
  /^[A-Z]{2}-[0-9]{2}-[0-9]{2}$/,
  /^[0-9]{2}-[0-9]{2}-[A-Z]{2}$/,
  /^[0-9]{2}-[A-Z]{2}-[0-9]{2}$/,
  /^[A-Z]{2}-[0-9]{2}-[A-Z]{2}$/,
  /^[A-Z]{2}-[A-Z]{2}-[0-9]{2}$/,
  /^[0-9]{2}-[A-Z]{2}-[A-Z]{2}$/,
  /^[0-9]{2}-[A-Z]{3}-[0-9]{1}$/,
  /^[0-9]{1}-[A-Z]{3}-[0-9]{2}$/,
  /^[A-Z]{2}-[0-9]{3}-[A-Z]{1}$/,
  /^[A-Z]{1}-[0-9]{3}-[A-Z]{2}$/,
  /^[A-Z]{3}-[0-9]{2}-[A-Z]{1}$/,
  /^[A-Z]{1}-[0-9]{2}-[A-Z]{3}$/,
  /^[0-9]{1}-[A-Z]{2}-[0-9]{3}$/,
  /^[0-9]{3}-[A-Z]{2}-[0-9]{1}$/,
];

function isValidDutchPlate(plate) {
  return DUTCH_PLATE_PATTERNS.some(p => p.test(plate));
}

function formatAndValidate(raw) {
  if (raw.length === 6) {
    const f = `${raw.substring(0, 2)}-${raw.substring(2, 4)}-${raw.substring(4, 6)}`;
    if (isValidDutchPlate(f)) return f;
  } else if (raw.length === 7) {
    const formats = [
      `${raw[0]}-${raw.substring(1, 4)}-${raw.substring(4, 7)}`,
      `${raw.substring(0, 2)}-${raw.substring(2, 5)}-${raw.substring(5, 7)}`,
      `${raw.substring(0, 3)}-${raw.substring(3, 5)}-${raw.substring(5, 7)}`,
    ];
    for (const f of formats) {
      if (isValidDutchPlate(f)) return f;
    }
  }
  return null;
}

function extractLicensePlate(text) {
  const upper = text.toUpperCase();

  // Zoek eerst patronen mét streepjes
  const dashPatterns = [
    /[A-Z0-9]{2}-[A-Z0-9]{2}-[A-Z0-9]{2}/g,
    /[A-Z0-9]{1}-[A-Z0-9]{3}-[A-Z0-9]{2}/g,
    /[A-Z0-9]{2}-[A-Z0-9]{3}-[A-Z0-9]{1}/g,
    /[A-Z0-9]{3}-[A-Z0-9]{2}-[A-Z0-9]{1}/g,
  ];
  for (const pat of dashPatterns) {
    const matches = [...upper.matchAll(pat)].map(m => m[0]);
    for (const m of matches) {
      if (isValidDutchPlate(m)) return m;
    }
  }

  // Zoek 6-7 alfanumerieke tekens aaneengesloten
  const cleaned = upper.replace(/[^A-Z0-9]/g, '');
  for (const len of [6, 7]) {
    for (let i = 0; i <= cleaned.length - len; i++) {
      const formatted = formatAndValidate(cleaned.substring(i, i + len));
      if (formatted) return formatted;
    }
  }

  return null;
}

// Laad Tesseract.js van CDN (eenmalig, geen npm-pakket nodig)
function loadTesseract() {
  return new Promise((resolve, reject) => {
    if (window.Tesseract) { resolve(window.Tesseract); return; }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js';
    script.onload = () => resolve(window.Tesseract);
    script.onerror = () => reject(new Error('Tesseract kon niet geladen worden'));
    document.head.appendChild(script);
  });
}

// ============================================================
// WebScanner component
// ============================================================
const WebScanner = () => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [cameraError, setCameraError] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrPhase, setOcrPhase] = useState(null); // null | 'lib-laden' | 'analyseren'
  const [ocrStatus, setOcrStatus] = useState(null); // null | 'found' | 'notfound'

  const [plateInput, setPlateInput] = useState('');

  const [scanResult, setScanResult] = useState(null); // { status, kenteken, organisatie, naam }
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState(null);

  // Auth check — lees sessie direct uit localStorage om Supabase lock-problemen te vermijden
  useEffect(() => {
    try {
      const projectRef = (process.env.REACT_APP_SUPABASE_URL || '')
        .replace('https://', '').split('.')[0];
      const sessionStr = localStorage.getItem(`sb-${projectRef}-auth-token`);
      if (sessionStr) {
        const session = JSON.parse(sessionStr);
        const expiresAt = session?.expires_at;
        if (session?.user && expiresAt && Date.now() / 1000 < expiresAt) {
          setUser(session.user);
        }
      }
    } catch (e) {
      // sessie niet leesbaar
    }
    setAuthLoading(false);
  }, []);

  // Camera start/stop
  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      setCameraError('Kan camera niet starten. Controleer de camerapermissies.');
    }
  }, []);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => {
    if (user) startCamera();
    return () => stopCamera();
  }, [user, startCamera, stopCamera]);

  // OCR: capture frame en scan
  const captureAndOcr = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    setOcrLoading(true);
    setOcrPhase(window.Tesseract ? 'analyseren' : 'lib-laden');
    setOcrStatus(null);
    setPlateInput('');
    setScanError(null);

    try {
      const canvas = canvasRef.current;
      const video = videoRef.current;

      // Bereken de uitsnede van het scan-kader in video-pixels.
      // Het kader is w-72 (288px) × h-20 (80px), gecentreerd in het video-element.
      // De video schaalt met object-cover: de grootste verhouding wint.
      const vw = video.videoWidth;
      const vh = video.videoHeight;

      if (!vw || !vh) throw new Error('Video nog niet gestart');

      const rect = video.getBoundingClientRect();
      const dw = rect.width;
      const dh = rect.height;

      if (!dw || !dh) throw new Error('Video heeft geen weergavegrootte');

      const scale = Math.max(dw / vw, dh / vh);
      const ox = (dw - vw * scale) / 2;
      const oy = (dh - vh * scale) / 2;

      const frameW = 288;
      const frameH = 80;
      const frameX = (dw - frameW) / 2;
      const frameY = (dh - frameH) / 2;

      const padding = 24;
      const cropX = Math.max(0, Math.round((frameX - ox) / scale - padding));
      const cropY = Math.max(0, Math.round((frameY - oy) / scale - padding));
      const cropW = Math.min(vw - cropX, Math.round(frameW / scale + 2 * padding));
      const cropH = Math.min(vh - cropY, Math.round(frameH / scale + 2 * padding));

      // Teken de uitsnede op de canvas, vergroot 2× voor betere OCR-kwaliteit
      const outW = cropW * 2;
      const outH = cropH * 2;
      canvas.width = outW;
      canvas.height = outH;
      canvas.getContext('2d').drawImage(video, cropX, cropY, cropW, cropH, 0, 0, outW, outH);
      const imageData = canvas.toDataURL('image/jpeg', 0.92);

      const Tesseract = await loadTesseract();
      setOcrPhase('analyseren');
      const { data: { text } } = await Tesseract.recognize(imageData, 'eng', { logger: () => {} });
      const plate = extractLicensePlate(text);
      if (plate) {
        setPlateInput(plate);
        setOcrStatus('found');
      } else {
        setOcrStatus('notfound');
      }
    } catch (err) {
      setScanError('OCR mislukt: ' + (err.message || 'onbekende fout'));
      setOcrStatus('notfound');
    } finally {
      setOcrLoading(false);
      setOcrPhase(null);
    }
  };

  // Scan registreren
  const handleScan = async () => {
    const kenteken = plateInput.trim().toUpperCase();
    if (!kenteken) {
      setScanError('Voer een kenteken in.');
      return;
    }

    setScanLoading(true);
    setScanError(null);
    setScanResult(null);

    try {
      const vehicle = await scanService.lookupVehicle(kenteken);
      const status = vehicle?.toegang ? 'toegestaan' : 'geweigerd';
      await scanService.insert(kenteken, status, user.id);

      setScanResult({
        kenteken,
        status,
        organisatie: vehicle?.organisatie || null,
        naam: vehicle
          ? `${vehicle.voornaam || ''} ${vehicle.achternaam || ''}`.trim() || null
          : null,
      });

      setPlateInput('');
      setOcrStatus(null);
    } catch (err) {
      setScanError('Fout bij registreren: ' + err.message);
    } finally {
      setScanLoading(false);
    }
  };

  const resetResult = () => {
    setScanResult(null);
    setScanError(null);
  };

  // ────────────────────────────────
  // Render: Auth laden
  // ────────────────────────────────
  if (authLoading) {
    return (
      <div className="min-h-screen bg-zf-dark flex items-center justify-center">
        <Loader className="animate-spin text-zf-accent" size={40} />
      </div>
    );
  }

  // ────────────────────────────────
  // Render: Niet ingelogd
  // ────────────────────────────────
  if (!user) {
    return (
      <div className="min-h-screen bg-zf-dark flex items-center justify-center p-6">
        <div className="bg-zf-darker rounded-2xl p-8 border border-gray-700 text-center max-w-sm w-full">
          <XCircle className="text-zf-accent mx-auto mb-4" size={48} />
          <h1 className="text-white text-2xl font-bold mb-2">Geen toegang</h1>
          <p className="text-gray-400 mb-6">Log eerst in via het portaal en open daarna de scanner opnieuw.</p>
          <a
            href="/"
            className="inline-block bg-zf-accent text-white px-6 py-3 rounded-lg font-medium hover:bg-zf-accent/80 transition-colors"
          >
            Naar portaal
          </a>
        </div>
      </div>
    );
  }

  // ────────────────────────────────
  // Render: Scan resultaat
  // ────────────────────────────────
  if (scanResult) {
    const approved = scanResult.status === 'toegestaan';
    return (
      <div
        className={`min-h-screen flex flex-col items-center justify-center p-6 ${approved ? 'bg-green-900' : 'bg-red-900'}`}
      >
        <div className="text-center max-w-sm w-full">
          {approved
            ? <CheckCircle className="text-green-300 mx-auto mb-4" size={80} />
            : <XCircle className="text-red-300 mx-auto mb-4" size={80} />
          }

          <p className={`text-4xl font-black mb-2 ${approved ? 'text-green-300' : 'text-red-300'}`}>
            {approved ? 'TOEGESTAAN' : 'GEWEIGERD'}
          </p>

          <p className="text-white text-3xl font-bold tracking-widest mb-6">
            {scanResult.kenteken}
          </p>

          {(scanResult.naam || scanResult.organisatie) && (
            <div className="bg-black/30 rounded-xl p-4 mb-6 text-left space-y-1">
              {scanResult.naam && (
                <p className="text-white font-medium">{scanResult.naam}</p>
              )}
              {scanResult.organisatie && (
                <p className="text-gray-300 text-sm">{scanResult.organisatie}</p>
              )}
            </div>
          )}

          {!scanResult.naam && !scanResult.organisatie && (
            <p className="text-gray-300 mb-6">Niet geregistreerd</p>
          )}

          <button
            onClick={resetResult}
            className="w-full bg-white/20 hover:bg-white/30 text-white px-6 py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-2 transition-colors"
          >
            <RotateCcw size={20} />
            Nieuw kenteken
          </button>
        </div>
      </div>
    );
  }

  // ────────────────────────────────
  // Render: Scanner
  // ────────────────────────────────
  return (
    <div className="h-screen bg-zf-dark flex flex-col overflow-hidden">

      {/* Header */}
      <div className="bg-zf-darker border-b border-gray-700 px-4 py-2 flex items-center gap-2 shrink-0">
        <ScanLine className="text-zf-accent" size={20} />
        <span className="text-white font-bold">Web Scanner</span>
      </div>

      {/* Camera — vult alle beschikbare ruimte */}
      <div className="relative flex-1 bg-black overflow-hidden">
        {cameraError ? (
          <div className="flex items-center justify-center h-full p-6 text-center">
            <div>
              <XCircle className="text-red-400 mx-auto mb-3" size={48} />
              <p className="text-gray-300 mb-4">{cameraError}</p>
              <button onClick={startCamera} className="bg-zf-accent text-white px-4 py-2 rounded-lg">
                Opnieuw proberen
              </button>
            </div>
          </div>
        ) : (
          <>
            <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            {/* Scan kader — iets boven midden zodat SCAN-knop er niet voor zit */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ paddingBottom: '100px' }}>
              <div className="w-72 h-20 border-4 border-zf-accent rounded-lg opacity-80" />
            </div>
          </>
        )}

        <canvas ref={canvasRef} className="hidden" />

        {/* OCR bezig overlay */}
        {ocrLoading && (
          <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-4">
            <Loader className="animate-spin text-zf-accent" size={48} />
            <p className="text-white font-semibold text-lg">
              {ocrPhase === 'lib-laden' ? 'Scanner laden...' : 'Kenteken lezen...'}
            </p>
            {ocrPhase === 'lib-laden' && (
              <p className="text-gray-400 text-sm">Eerste keer duurt even</p>
            )}
          </div>
        )}

        {/* Floating SCAN knop */}
        {!ocrLoading && !cameraError && (
          <div className="absolute bottom-6 left-0 right-0 flex justify-center">
            <button
              onClick={captureAndOcr}
              className="bg-zf-accent text-white px-12 py-5 rounded-full font-black text-2xl shadow-2xl active:scale-95 transition-transform flex items-center gap-3"
            >
              <Camera size={28} />
              SCAN
            </button>
          </div>
        )}
      </div>

      {/* Onderste balk: meldingen + invoer + registreren */}
      <div className="bg-zf-darker border-t border-gray-700 px-4 pt-2 pb-3 space-y-2 shrink-0">

        {/* OCR resultaat */}
        {!ocrLoading && ocrStatus === 'found' && (
          <div className="bg-green-600/20 border border-green-600 text-green-400 px-3 py-1.5 rounded-lg text-sm flex items-center gap-2">
            <CheckCircle size={14} /> Kenteken herkend: <span className="font-bold">{plateInput}</span>
          </div>
        )}
        {!ocrLoading && ocrStatus === 'notfound' && (
          <div className="bg-orange-600/20 border border-orange-600 text-orange-400 px-3 py-1.5 rounded-lg text-sm">
            Geen kenteken herkend — voer handmatig in
          </div>
        )}

        {/* Foutmelding */}
        {scanError && (
          <div className="bg-red-900/50 border border-red-700 text-red-300 px-3 py-1.5 rounded-lg text-sm">
            {scanError}
          </div>
        )}

        {/* Kenteken invoer */}
        <input
          type="text"
          value={plateInput}
          onChange={(e) => { setPlateInput(e.target.value.toUpperCase()); setScanError(null); setOcrStatus(null); }}
          placeholder="AB-12-CD"
          className="w-full bg-zf-dark border border-gray-600 text-white placeholder-gray-500 px-4 py-2 rounded-xl text-center text-2xl font-bold tracking-widest focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none"
          maxLength={9}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
        />

        {/* Registreer knop */}
        <button
          onClick={handleScan}
          disabled={scanLoading || !plateInput.trim()}
          className="w-full bg-zf-accent text-white py-3 rounded-xl font-bold text-lg flex items-center justify-center gap-3 hover:bg-zf-accent/80 transition-colors disabled:opacity-50"
        >
          {scanLoading ? (
            <><Loader className="animate-spin" size={22} /> Registreren...</>
          ) : (
            <><CheckCircle size={22} /> Registreer scan</>
          )}
        </button>
      </div>
    </div>
  );
};

export default WebScanner;
